import { useEffect, useRef, useState } from 'react';
import { App } from '@capacitor/app';
import { Geolocation } from '@capacitor/geolocation';
import api from '@/lib/api';
import { reverseGeocode } from '@/lib/uploads';

const MIN_SEND_INTERVAL_MS = 15000;
const REVERSE_GEOCODE_DISTANCE_M = 200;
const REVERSE_GEOCODE_INTERVAL_MS = 5 * 60 * 1000;

function distanceMeters(lat1, lng1, lat2, lng2) {
  if ([lat1, lng1, lat2, lng2].some((v) => !Number.isFinite(v))) return Infinity;
  const toRad = (v) => (v * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 6371000 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export default function useConsumerLocationSync(me) {
  const [liveLocation, setLiveLocation] = useState(() => ({
    label: me?.location || me?.city || '',
    city: me?.city || '',
    locating: false,
    lastUpdatedAt: null,
  }));
  const watchIdRef = useRef(null);
  const sendingRef = useRef(false);
  const lastSentAtRef = useRef(0);
  const lastCoordsRef = useRef(null);
  const lastGeocodedAtRef = useRef(0);
  const lastLabelRef = useRef(me?.location || me?.city || '');

  useEffect(() => {
    lastLabelRef.current = me?.location || me?.city || '';
    setLiveLocation((current) => ({
      ...current,
      label: me?.location || me?.city || current.label || '',
      city: me?.city || current.city || '',
    }));
  }, [me?.location, me?.city]);

  useEffect(() => {
    if (!me?.id || me?.role !== 'consumer') return undefined;

    let cancelled = false;
    let appListener = null;

    const updatePosition = async (position, force = false) => {
      if (cancelled || !position?.coords || sendingRef.current) return;

      const lat = Number(position.coords.latitude);
      const lng = Number(position.coords.longitude);
      const accuracy = Number(position.coords.accuracy);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

      const now = Date.now();
      const previous = lastCoordsRef.current;
      const moved = previous ? distanceMeters(previous.lat, previous.lng, lat, lng) : Infinity;

      if (!force && now - lastSentAtRef.current < MIN_SEND_INTERVAL_MS && moved < 25) return;

      sendingRef.current = true;
      try {
        const shouldReverse =
          !lastLabelRef.current ||
          moved >= REVERSE_GEOCODE_DISTANCE_M ||
          now - lastGeocodedAtRef.current >= REVERSE_GEOCODE_INTERVAL_MS;

        let geo = null;
        if (shouldReverse) {
          geo = await reverseGeocode(lat, lng);
          if (geo) {
            lastGeocodedAtRef.current = now;
            lastLabelRef.current = geo.short || geo.label || lastLabelRef.current;
          }
        }

        const payload = {
          latitude: lat,
          longitude: lng,
          accuracy_m: Number.isFinite(accuracy) ? accuracy : null,
          source: 'gps',
        };

        if (geo?.label || geo?.short) payload.location = geo.short || geo.label;
        if (geo?.city) payload.city = geo.city;

        await api.patch('/users/me/location', payload);

        lastSentAtRef.current = now;
        lastCoordsRef.current = { lat, lng };

        if (!cancelled) {
          setLiveLocation({
            label: geo?.short || geo?.label || lastLabelRef.current || me.location || me.city || '',
            city: geo?.city || me.city || '',
            locating: false,
            lastUpdatedAt: new Date().toISOString(),
          });
        }
      } catch {
        // Best-effort sync; retry on the next GPS update/app resume.
      } finally {
        sendingRef.current = false;
      }
    };

    const getInitialPosition = async () => {
      setLiveLocation((current) => ({ ...current, locating: true }));
      try {
        const permission = await Geolocation.requestPermissions();
        if (permission.location !== 'granted' && permission.coarseLocation !== 'granted') {
          setLiveLocation((current) => ({ ...current, locating: false }));
          return false;
        }

        const position = await Geolocation.getCurrentPosition({
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 5000,
        });
        await updatePosition(position, true);
        return true;
      } catch {
        setLiveLocation((current) => ({ ...current, locating: false }));
        return false;
      }
    };

    const startWatch = async () => {
      if (watchIdRef.current) return;
      try {
        watchIdRef.current = await Geolocation.watchPosition(
          {
            enableHighAccuracy: true,
            timeout: 15000,
            maximumAge: 5000,
          },
          async (position) => {
            if (position) await updatePosition(position);
          },
        );
      } catch {
        // Initial acquisition still provides a one-shot location.
      }
    };

    const stopWatch = async () => {
      if (!watchIdRef.current) return;
      try {
        await Geolocation.clearWatch({ id: watchIdRef.current });
      } catch {}
      watchIdRef.current = null;
    };

    const handleAppState = async ({ isActive }) => {
      if (isActive && !cancelled) await getInitialPosition();
    };

    const init = async () => {
      await getInitialPosition();
      if (!cancelled) await startWatch();
      if (!cancelled) {
        appListener = await App.addListener('appStateChange', handleAppState);
      }
    };

    init();

    return () => {
      cancelled = true;
      stopWatch();
      if (appListener) appListener.remove();
    };
  }, [me?.id, me?.role, me?.city, me?.location]);

  return liveLocation;
}
